import React, { FC, useCallback } from 'react'
import styled from '@emotion/styled'
import { PreparedStagingOrder } from 'store/selection/selectionTypes'
import { CounterpartyInstruction, TradeConditionFlag, TradingEntityWithAlias } from 'api'
import { getAssetDescription, PositionsGrouping } from './rfqPanelUtils'
import { AxeTradeConditionButtons } from 'view/shared/entity'
import { PositionListItem } from './PositionListItem'
import { TradingEntityWithAliasHelper } from 'model'
import { ListContainer } from './rfqPanelStyles'
import { PositionsSectionProps } from './PositionsSection'
import { Translation } from 'locale'
import { notNilFilter } from 'utils'

interface PositionListProps extends PositionsSectionProps {
  chosenGrouping: PositionsGrouping
  toggleItemCollapse: (itemId: string) => void
  getIsItemCollapsed: (itemId: string) => boolean
}

const SubItemNameContainer = styled.div`
  display: inline-block;
  padding-left: 6px;
  width: 50%;
  height: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const Subitem = styled.li`
  display: flex;
  flex: 1 auto;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 44px;
  border-bottom: 1px solid #e1e3e7;
  justify-content: space-between;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-right: 8px;
  &:only-child {
    border-top: 1px solid #e1e3e7;
    border-bottom: none;
  }
  &:first-of-type {
    border-top: 1px solid #e1e3e7;
  }
  &:last-child {
    border-bottom: none;
  }
`

export const PositionList: FC<PositionListProps> = props => {
  const {
    readOnly,
    chosenGrouping,
    counterpartyInstructions,
    userPreference,
    ordersToPrepare,
    onChangePosition,
    getIsItemCollapsed,
    toggleItemCollapse,
    getTradingEntity
  } = props

  const selectedCounterpartyIds = counterpartyInstructions.map(instruction => instruction.counterpartyId)

  const onSetAxeTradeCondition = useCallback(
    (isAxed: boolean, tradeConditionFlag: TradeConditionFlag, orderIds: string[], counterpartiesId: string[]) => {
      onChangePosition(orderIds, counterpartiesId, isAxed, tradeConditionFlag)
    },
    [onChangePosition]
  )

  const getAssets = useCallback(
    (preparedStagingOrders: PreparedStagingOrder[]) => {
      return preparedStagingOrders.map((orderToPrepare, orderIndex) => {
        const orderId = orderToPrepare.orderDetail.order.orderId
        const fundId = orderToPrepare.orderDetail.order.fundId
        const counterpartyInstructions = orderToPrepare.negotiationInstruction.counterpartyInstructions
        return (
          <PositionListItem
            id={orderId}
            key={orderId}
            index={orderIndex}
            readOnly={readOnly}
            chosenGrouping={chosenGrouping}
            toggleItemCollapse={toggleItemCollapse}
            getIsItemCollapsed={getIsItemCollapsed}
            onSetAxeTradeCondition={onSetAxeTradeCondition}
            counterpartyInstructions={counterpartyInstructions}
            title={getAssetDescription(orderToPrepare.orderDetail, userPreference)}
            subItems={fundId ? getAssetSubItems(orderId, counterpartyInstructions) : []}
          />
        )
      })
    },
    [onSetAxeTradeCondition, selectedCounterpartyIds, getIsItemCollapsed, toggleItemCollapse, userPreference, chosenGrouping, readOnly]
  )

  const getCounterparties = useCallback(
    (counterpartyInstructions: CounterpartyInstruction[]) => {
      return counterpartyInstructions.map((ci, counterpartyIndex) => {
        const counterparty = { ...getTradingEntity(ci.counterpartyId), alias: ci.counterpartyAlias } as TradingEntityWithAlias

        const selectedInstructions = ordersToPrepare
          .map(orderToPrepare =>
            orderToPrepare.negotiationInstruction.counterpartyInstructions.find(
              instruction => instruction.counterpartyId === ci.counterpartyId
            )
          )
          .filter(notNilFilter)

        return (
          <PositionListItem
            id={ci.counterpartyId}
            key={ci.counterpartyId}
            readOnly={readOnly}
            index={counterpartyIndex}
            chosenGrouping={chosenGrouping}
            ordersToPrepare={ordersToPrepare}
            toggleItemCollapse={toggleItemCollapse}
            getIsItemCollapsed={getIsItemCollapsed}
            counterpartyInstructions={selectedInstructions}
            onSetAxeTradeCondition={onSetAxeTradeCondition}
            title={counterparty ? TradingEntityWithAliasHelper.getName(counterparty) : ''}
            subItems={getCounterpartySubItems(ordersToPrepare, ci.counterpartyId)}
          />
        )
      })
    },
    [ordersToPrepare, getIsItemCollapsed, toggleItemCollapse, onSetAxeTradeCondition, chosenGrouping, readOnly]
  )

  const getAssetSubItems = useCallback(
    (orderId: string, counterpartyInstructions: CounterpartyInstruction[]) => {
      return counterpartyInstructions.map(({ counterpartyId }) => {
        const counterpartyInstruction: CounterpartyInstruction = counterpartyInstructions.find(
          ci => ci.counterpartyId === counterpartyId
        ) || {
          counterpartyId: counterpartyId,
          counterpartyAlias: null,
          isAxed: false,
          axeId: null,
          internalAxeId: null,
          tradeConditionFlag: TradeConditionFlag.NONE,
          primeBrokerId: null,
          repoType: null,
          masterLegalAgreementType: null
        }

        const counterparty = { ...getTradingEntity(counterpartyId)!, alias: counterpartyInstruction.counterpartyAlias }

        return (
          counterparty && (
            <Subitem data-test-id={'SendRFQPanel-PositionListItem-Subitem'} key={counterpartyId}>
              <SubItemNameContainer data-test-id={'SendRFQPanel-PositionListItem-Subitem-Name'}>
                {TradingEntityWithAliasHelper.getName(counterparty)}
              </SubItemNameContainer>
              <AxeTradeConditionButtons
                dataTestId={'SendRFQPanel-PositionListItem-Subitem-AxeTradeConditionButtons'}
                isAxed={counterpartyInstruction.isAxed}
                tradeConditionFlag={counterpartyInstruction.tradeConditionFlag}
                readOnly={readOnly}
                onUpdate={(isAxed, tradeConditionFlag) =>
                  onSetAxeTradeCondition(isAxed, tradeConditionFlag, [orderId], [counterpartyInstruction.counterpartyId])
                }
              />
            </Subitem>
          )
        )
      })
    },
    [onSetAxeTradeCondition, readOnly]
  )

  const getCounterpartySubItems = useCallback(
    (preparedStagingOrders: PreparedStagingOrder[], currentCounterpartyId: string) => {
      return preparedStagingOrders
        .map(order => {
          const instruction = order.negotiationInstruction.counterpartyInstructions.find(
            counterpartyInstruction => counterpartyInstruction.counterpartyId === currentCounterpartyId
          )
          const orderId = order.orderDetail.order.orderId
          return (
            instruction && (
              <Subitem data-test-id={'SendRFQPanel-PositionsListItem-Subitem'} key={currentCounterpartyId}>
                <SubItemNameContainer data-test-id={'SendRFQPanel-PositionListItem-Subitem-Name'}>
                  {getAssetDescription(order.orderDetail, userPreference)}
                </SubItemNameContainer>
                <AxeTradeConditionButtons
                  dataTestId={'SendRFQPanel-PositionListItem-Subitem-AxeTradeConditionButtons'}
                  isAxed={instruction.isAxed}
                  tradeConditionFlag={instruction.tradeConditionFlag}
                  readOnly={readOnly}
                  onUpdate={(isAxed, tradeConditionFlag) =>
                    onSetAxeTradeCondition(isAxed, tradeConditionFlag, [orderId], [instruction.counterpartyId])
                  }
                />
              </Subitem>
            )
          )
        })
        .filter(notNilFilter)
    },
    [onSetAxeTradeCondition, readOnly, userPreference]
  )

  const getList = useCallback(() => {
    switch (chosenGrouping) {
      case Translation.SC_GROUP_BY_ASSETS:
        return getAssets(ordersToPrepare)
      case Translation.SC_GROUP_BY_COUNTERPARTIES:
        return getCounterparties(counterpartyInstructions)
      default:
        throw new Error(`Unknown grouping selected ${chosenGrouping}`)
    }
  }, [getAssets, getCounterparties, chosenGrouping, ordersToPrepare, counterpartyInstructions])

  return <ListContainer>{getList()}</ListContainer>
}
